import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import ImageUploader from '../../components/ImageUploader';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Award, RefreshCw, Star } from 'lucide-react';

const AdminSpotlights = () => {
  const { token } = useAuth();
  const [spotlights, setSpotlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', title: '', achievement: '', bio: '', image_url: '' });

  const fetchSpotlights = async () => {
    try {
      const response = await axios.get(`${API}/spotlights`);
      setSpotlights(response.data);
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSpotlights(); }, []);

  const handleSubmit = async () => {
    if (!formData.name || !formData.achievement || !formData.bio) {
      toast.error('Please fill in name, achievement, and bio');
      return;
    }

    try {
      if (editing) {
        await axios.put(`${API}/spotlights/${editing}`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Spotlight updated!');
      } else {
        await axios.post(`${API}/spotlights`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Spotlight created!');
      }
      setShowDialog(false);
      setFormData({ name: '', title: '', achievement: '', bio: '', image_url: '' });
      setEditing(null);
      fetchSpotlights();
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleEdit = (item) => {
    setFormData({ name: item.name, title: item.title || '', achievement: item.achievement, bio: item.bio, image_url: item.image_url || '' });
    setEditing(item.id);
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this spotlight?')) return;
    try {
      await axios.delete(`${API}/spotlights/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Spotlight deleted!');
      fetchSpotlights();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Award className="w-6 h-6 text-plp-gold" /> Resident Spotlights
          </h1>
          <p className="text-slate-600">Highlight community members and their achievements</p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) { setEditing(null); setFormData({ name: '', title: '', achievement: '', bio: '', image_url: '' }); }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-plp-blue hover:bg-plp-blue-light">
              <Plus className="w-4 h-4 mr-2" /> New Spotlight
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">{editing ? 'Edit Spotlight' : 'New Spotlight'}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <ImageUploader 
                label="Photo"
                value={formData.image_url}
                onChange={(url) => setFormData(p => ({ ...p, image_url: url }))}
              />
              
              <div>
                <Label>Full Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Resident's name" />
              </div>
              <div>
                <Label>Title/Role</Label>
                <Input value={formData.title} onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))} placeholder="e.g., Local Business Owner, Teacher" />
              </div>
              <div>
                <Label>Achievement *</Label>
                <Input value={formData.achievement} onChange={(e) => setFormData(p => ({ ...p, achievement: e.target.value }))} placeholder="What they achieved or contributed" />
              </div>
              <div>
                <Label>Bio / Story *</Label>
                <Textarea value={formData.bio} onChange={(e) => setFormData(p => ({ ...p, bio: e.target.value }))} rows={5} placeholder="Tell their story..." />
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowDialog(false)} className="flex-1">Cancel</Button>
                <Button onClick={handleSubmit} className="flex-1 bg-plp-blue">{editing ? 'Update' : 'Create Spotlight'}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-plp-blue" /></div>
      ) : spotlights.length === 0 ? (
        <Card className="bg-white">
          <CardContent className="py-12 text-center">
            <Award className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No spotlights yet</p>
            <Button onClick={() => setShowDialog(true)} variant="link" className="text-plp-blue mt-2">
              Create your first spotlight
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {spotlights.map((item) => (
            <Card key={item.id} className="bg-white overflow-hidden">
              <CardContent className="p-0">
                <div className="relative">
                  {item.image_url ? (
                    <img src={item.image_url} alt={item.name} className="w-full h-48 object-cover" />
                  ) : (
                    <div className="w-full h-48 bg-gradient-to-br from-plp-gold/20 to-plp-blue/20 flex items-center justify-center">
                      <Award className="w-16 h-16 text-plp-gold/50" />
                    </div>
                  )}
                  <div className="absolute top-2 right-2 flex gap-1">
                    <Button variant="secondary" size="icon" className="w-8 h-8 bg-white/90" onClick={() => handleEdit(item)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="secondary" size="icon" className="w-8 h-8 bg-white/90 text-red-500" onClick={() => handleDelete(item.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-heading font-bold text-lg">{item.name}</h3>
                  {item.title && <p className="text-sm text-plp-blue">{item.title}</p>}
                  <div className="flex items-center gap-2 mt-2">
                    <Star className="w-4 h-4 text-plp-gold" />
                    <p className="text-sm font-medium text-emerald-600">{item.achievement}</p>
                  </div>
                  <p className="text-sm text-slate-600 mt-2 line-clamp-3">{item.bio}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminSpotlights;
